import { HttpParams } from '@angular/common/http';
import { Component, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';
import { SteppedComponent } from 'projects/reg-hub-client/src/interfaces/stepped-component';
import { Address, AddressFormComponent, Brand, ContactDetails, CorporationRepositoryService, eIdentityVerificationSource, EmptyEntityService, ePartyType, instantValidationThrottle, Order, OrderManagerService, Party, PartyForCreation, ReusableParty, ReusablePartyRepositoryService, ValidationProblem, ValidationService } from 'reg-hub-common';
import { BehaviorSubject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-ultimate-beneficial-ownership-report',
  templateUrl: './ultimate-beneficial-ownership-report.component.html',
  styleUrl: './ultimate-beneficial-ownership-report.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UltimateBeneficialOwnershipReportComponent extends SteppedComponent {
  @Input() isFormEnabled: boolean = true;
  @ViewChild(AddressFormComponent) addressFormComponent!: AddressFormComponent;

  protected ultimateBeneficialOwnershipDetails!: FormGroup;

  party?: Party;

  private orderToValidate: Order | null = null;

  reusableSecuredParties: ReusableParty[] = [];
  brands: Brand[] = [];
  brandLogoUrls: { [brandID: string]: string; } = {};

  firstNameError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
  lastNameError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
  emailError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
  phoneNumberError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
  dobError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
  displayNameError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);


  constructor(
    private reusablePartyRepo: ReusablePartyRepositoryService,
    private corporationRepo: CorporationRepositoryService,
    private formBuilder: FormBuilder,
    private emptyEntitysService: EmptyEntityService,
    orderManager: OrderManagerService,
    validationService: ValidationService) {
    super(orderManager, validationService);
  }

  protected override init(order: Order): void {
    super.init(order);
    this.party = this.order.parties?.find(p => p.partyTypeID === ePartyType.individualAttestor);

    // refactor at some point to have the same form field names as the party itself
    // so we can just map the form directly into the party
    this.ultimateBeneficialOwnershipDetails = this.formBuilder.group({
      displayedCorpName: [this.order.ultimateBeneficialOwnershipRequest?.displayedCorpName],
      attestorFirstName: [this.party?.firstName],
      attestorMiddleName: [this.party?.middleName],
      attestorLastName: [this.party?.lastName],
      attestorDateOfBirth: [this.party?.dateOfBirth],
      attestorEmail: [this.party?.contactDetails?.email],
      attestorPhoneNumber: [this.party?.contactDetails?.phoneNumber],
      hasOneNameOnly: [this.party?.hasOneNameOnly],
      sendTextInFrench: [this.order.ultimateBeneficialOwnershipRequest?.sendTextInFrench ?? false],
      brandID: [this.order.ultimateBeneficialOwnershipRequest?.brandID ?? null]
    });

    this.toggleHasOneName();

    if (!this.isFormEnabled) {
      this.ultimateBeneficialOwnershipDetails.disable();
      this.addressFormComponent.addressDetails.disable();
    }

    this.reusablePartyRepo.populateBaseLink([this.order.orderGroup!.corporationID]);
    var reusablePartyParams = new HttpParams().set('isVisible', true)
      .set('pageSize', 100)
      .append('PartyTypeIDs', 'BusinessSecuredParty');
    this.reusablePartyRepo.getFirstPageSubscribable(reusablePartyParams).subscribe(parties => {
      this.reusableSecuredParties = parties;
    });

    // Brands are optional - the dropdown is only shown when the corporation has at least one.
    this.corporationRepo.getBrands(this.order.orderGroup!.corporationID).subscribe({
      next: brands => {
        this.brands = brands ?? [];
        this.loadBrandLogos();
      },
      error: () => this.brands = []
    });

    this.ultimateBeneficialOwnershipDetails.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        instantValidationThrottle())
      .subscribe(() => this.validate(this.getOrderToValidate()));
  }

  ngAfterViewInit(): void {
    this.addressFormComponent.addressDetails.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        instantValidationThrottle())
      .subscribe(() => this.validate(this.getOrderToValidate()));
  }

  private getOrderToValidate(): Order {
    if (!this.orderToValidate) {
      this.orderToValidate = this.copyOrder(this.order);
    }

    const oneNameOnly = this.ultimateBeneficialOwnershipDetails.get('hasOneNameOnly')?.value;

    if (!oneNameOnly) {
      this.orderToValidate.parties!.at(0)!.firstName = this.ultimateBeneficialOwnershipDetails.get('attestorFirstName')!.value;
      this.orderToValidate.parties!.at(0)!.middleName = this.ultimateBeneficialOwnershipDetails.get('attestorMiddleName')!.value;
    }
    this.orderToValidate.parties!.at(0)!.lastName = this.ultimateBeneficialOwnershipDetails.get('attestorLastName')!.value;
    this.orderToValidate.parties!.at(0)!.contactDetails!.email = this.ultimateBeneficialOwnershipDetails.get('attestorEmail')!.value;
    this.orderToValidate.parties!.at(0)!.hasOneNameOnly = oneNameOnly;

    if (this.showAttestorAddressFields()) {
      this.orderToValidate.parties!.at(0)!.dateOfBirth = this.ultimateBeneficialOwnershipDetails.get('attestorDateOfBirth')!.value;
      this.orderToValidate.parties!.at(0)!.contactDetails!.phoneNumber = this.ultimateBeneficialOwnershipDetails.get('attestorPhoneNumber')!.value;
      this.orderToValidate.parties!.at(0)!.contactDetails!.address!.streetNumber = this.addressFormComponent.addressDetails.get('streetNumber')!.value;
      this.orderToValidate.parties!.at(0)!.contactDetails!.address!.streetName = this.addressFormComponent.addressDetails.get('streetName')!.value;
      this.orderToValidate.parties!.at(0)!.contactDetails!.address!.unit = this.addressFormComponent.addressDetails.get('unit')!.value;
      this.orderToValidate.parties!.at(0)!.contactDetails!.address!.city = this.addressFormComponent.addressDetails.get('city')!.value;
      this.orderToValidate.parties!.at(0)!.contactDetails!.address!.postalCode = this.addressFormComponent.addressDetails.get('postalCode')!.value;
      this.orderToValidate.parties!.at(0)!.contactDetails!.address!.countryCode = this.addressFormComponent.addressDetails.get('countryCode')!.value;
      this.orderToValidate.parties!.at(0)!.contactDetails!.address!.jurisdiction = this.addressFormComponent.addressDetails.get('jurisdiction')!.value;
    }

    if (this.showDisplayedCorpNameField()) {
      this.orderToValidate.ultimateBeneficialOwnershipRequest!.displayedCorpName = this.ultimateBeneficialOwnershipDetails.get('displayedCorpName')?.value;
    }
    return this.orderToValidate!;
  }

  public override pushErrors(errors: ValidationProblem[] | undefined): void {
    if (!this.party?.hasOneNameOnly) {
      this.firstNameError$.next(errors?.filter(error => error.path.includes('/name') || error.path.includes('/firstname')).at(0)?.userFriendlyMessage);
    }
    this.lastNameError$.next(errors?.filter(error => error.path.includes('/name') || error.path.includes('/lastname')).at(0)?.userFriendlyMessage);
    this.dobError$.next(errors?.filter(error => error.path.includes('/dateofbirth')).at(0)?.userFriendlyMessage);
    this.emailError$.next(errors?.filter(error => error.path.includes('/email')).at(0)?.userFriendlyMessage);
    this.phoneNumberError$.next(errors?.filter(error => error.path.includes('/phonenumber')).at(0)?.userFriendlyMessage);
    this.displayNameError$.next(errors?.filter(error => error.path.includes('displayedcorpname')).at(0)?.userFriendlyMessage);
  }

  public override getValidationState(errors: ValidationProblem[] | undefined, order: Order): StepValidationState {
    return (errors?.length ?? 0) === 0 ? StepValidationState.ValidationSuccess : StepValidationState.ValidationError;
  }

  public override onSaving(): void {
    if (!this.party) {
      this.party = this.emptyEntitysService.getEmptyParty(ePartyType.individualAttestor);
    }

    this.order.parties = [this.party];

    const oneNameOnly = this.ultimateBeneficialOwnershipDetails.get('hasOneNameOnly')?.value;
    const sendTextInFrench = this.ultimateBeneficialOwnershipDetails.get('sendTextInFrench')?.value;

    if (!oneNameOnly) {
      this.party.firstName = this.ultimateBeneficialOwnershipDetails.get('attestorFirstName')?.value;
      this.party.middleName = this.ultimateBeneficialOwnershipDetails.get('attestorMiddleName')?.value;
    }
    this.party.lastName = this.ultimateBeneficialOwnershipDetails.get('attestorLastName')?.value;
    this.party.hasOneNameOnly = oneNameOnly;
    this.order.ultimateBeneficialOwnershipRequest!.sendTextInFrench = sendTextInFrench;

    if (this.showBrandDropdown()) {
      this.order.ultimateBeneficialOwnershipRequest!.brandID = this.ultimateBeneficialOwnershipDetails.get('brandID')?.value ?? null;
    }

    if (!this.party.contactDetails) {
      this.party.contactDetails = this.getEmptyContactDetails();
    }

    this.party.contactDetails.email = this.ultimateBeneficialOwnershipDetails.get('attestorEmail')?.value;

    if (this.showAttestorPersonalInfoFields()) {
      this.party.dateOfBirth = this.ultimateBeneficialOwnershipDetails.get('attestorDateOfBirth')?.value;
      this.party.contactDetails.phoneNumber = this.ultimateBeneficialOwnershipDetails.get('attestorPhoneNumber')?.value;
    }

    if (this.showAttestorAddressFields()) {
      if (!this.party.contactDetails.address) {
        this.party.contactDetails.address = this.getEmptyAddress();
      }
      this.party.contactDetails.address.streetNumber = this.addressFormComponent.addressDetails.get('streetNumber')?.value;
      this.party.contactDetails.address.streetName = this.addressFormComponent.addressDetails.get('streetName')?.value;
      this.party.contactDetails.address.unit = this.addressFormComponent.addressDetails.get('unit')?.value;
      this.party.contactDetails.address.city = this.addressFormComponent.addressDetails.get('city')?.value;
      this.party.contactDetails.address.postalCode = this.addressFormComponent.addressDetails.get('postalCode')?.value;
      this.party.contactDetails.address.countryCode = this.addressFormComponent.addressDetails.get('countryCode')?.value;
      this.party.contactDetails.address.jurisdiction = this.addressFormComponent.addressDetails.get('jurisdiction')?.value;
    }

    if (this.showDisplayedCorpNameField()) {
      this.order.ultimateBeneficialOwnershipRequest!.displayedCorpName = this.ultimateBeneficialOwnershipDetails.get('displayedCorpName')?.value;
    }
  }

  showAttestorPersonalInfoFields(): boolean {
    return (this.order.identityVerificationDetails?.sources.some(s => s.name == eIdentityVerificationSource.EfxAmlSingleSource) ?? false) ||
      (this.order.identityVerificationDetails?.sources.some(s => s.name == eIdentityVerificationSource.EfxDvs) ?? false) ||
      (this.order.identityVerificationDetails?.sources.some(s => s.name == eIdentityVerificationSource.TuCreditBureau) ?? false);
  }

  showAttestorAddressFields(): boolean {
    return (this.order.identityVerificationDetails?.sources.some(s => s.name == eIdentityVerificationSource.EfxAmlSingleSource) ?? false) ||
      (this.order.identityVerificationDetails?.sources.some(s => s.name == eIdentityVerificationSource.TuCreditBureau) ?? false);
  }

  showDisplayedCorpNameField(): boolean {
    return this.order.ultimateBeneficialOwnershipRequest!.displayedCorpName != null;
  }

  showBrandDropdown(): boolean {
    return this.brands.length > 0;
  }

  private loadBrandLogos(): void {
    this.brandLogoUrls = {};
    const corporationID = this.order.orderGroup?.corporationID;
    if (!corporationID) {
      return;
    }

    for (const brand of this.brands) {
      if (!brand.logoDocumentID) {
        continue;
      }

      this.corporationRepo.getBrandDocument(corporationID, brand.id).subscribe({
        next: doc => {
          if (doc?.base64) {
            const type = `${doc.documentTypeID}`.toUpperCase();
            const mime = (type === 'JPG' || type === 'JPEG' || type === '9') ? 'image/jpeg' : 'image/png';
            this.brandLogoUrls[brand.id] = `data:${mime};base64,${doc.base64}`;
          }
        },
        error: () => { /* missing logo falls back to the colour swatch */ }
      });
    }
  }

  useReusableSecuredParty(party: PartyForCreation) {
    this.ultimateBeneficialOwnershipDetails.get('displayedCorpName')!.setValue(party.busName);
  }

  showEmail(): boolean {
    return this.order.ultimateBeneficialOwnershipRequest!.sendEmail;
  }

  showTextMessageInFrenchCheckbox(): boolean {
    return this.order.identityVerificationDetails?.sources.some(s => s.name == eIdentityVerificationSource.EfxDvs) ?? false;
  }

  protected getEmptyContactDetails(): ContactDetails {
    return {
      id: '',
      email: '',
      phoneNumber: '',
      faxNumber: '',
      address: this.getEmptyAddress()
    };
  }

  protected toggleHasOneName(): void {
    if (this.ultimateBeneficialOwnershipDetails.get('hasOneNameOnly')?.value) {
      this.ultimateBeneficialOwnershipDetails.get('attestorFirstName')?.disable({ emitEvent: false });
      this.ultimateBeneficialOwnershipDetails.get('attestorMiddleName')?.disable({ emitEvent: false });
    } else {
      this.ultimateBeneficialOwnershipDetails.get('attestorFirstName')?.enable({ emitEvent: false });
      this.ultimateBeneficialOwnershipDetails.get('attestorMiddleName')?.enable({ emitEvent: false });
    }
  }

  protected shouldShowOneNameOnlyOption(): boolean {
    return this.order.identityVerificationDetails?.sources?.some(s => s.name == eIdentityVerificationSource.EntrustDvs) ?? false;
  }

  protected getEmptyAddress(): Address {
    return {
      id: '',
      unit: '',
      streetNumber: '',
      streetName: '',
      city: '',
      postalCode: '',
      jurisdiction: '',
      countryCode: ''
    };
  }
}
